// Lightweight Web Audio engine: synthesized retro SFX + looping chiptune music.
// No audio files required.
import { vibrate } from "./haptics";

const MELODIES = [
  // managua (bright)
  { tempo: 200, scale: [523, 587, 659, 784, 659, 587, 523, 392, 440, 523, 659, 587, 523, 440, 392, 0], bass: [131, 0, 0, 0, 165, 0, 0, 0, 175, 0, 0, 0, 196, 0, 0, 0] },
  // granada (warm)
  { tempo: 180, scale: [440, 0, 523, 587, 659, 0, 587, 523, 494, 0, 587, 659, 698, 659, 587, 0], bass: [110, 0, 0, 0, 131, 0, 0, 0, 147, 0, 0, 0, 110, 0, 0, 0] },
  // sanjuan (sunset, faster)
  { tempo: 160, scale: [659, 784, 880, 784, 659, 587, 659, 523, 587, 659, 784, 880, 988, 880, 784, 659], bass: [165, 0, 165, 0, 196, 0, 196, 0, 175, 0, 175, 0, 147, 0, 147, 0] },
  // volcano boarding (fast Nicaraguan marimba/cumbia — high speed)
  { tempo: 112, type: "triangle", scale: [659, 784, 880, 988, 880, 784, 1047, 988, 880, 784, 659, 784, 880, 1047, 988, 784], bass: [262, 0, 392, 0, 262, 0, 392, 0, 294, 0, 440, 0, 294, 0, 440, 0] },
];

export class AudioManager {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.muted = false;
    this.volume = 0.6; // user SFX volume 0..1
    this.timer = null;
    this.step = 0;
    this.theme = 0;
  }

  _gain() {
    return this.muted ? 0 : 0.35 * this.volume;
  }

  init() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = this._gain();
    this.master.connect(this.ctx.destination);
  }

  resume() {
    this.init();
    if (this.ctx && this.ctx.state === "suspended") this.ctx.resume();
  }

  setMuted(m) {
    this.muted = m;
    if (this.master) this.master.gain.value = this._gain();
  }

  setVolume(v) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.master) this.master.gain.value = this._gain();
  }

  tone(freq, dur, type = "square", vol = 0.3, sweepTo = null, delay = 0) {
    if (!this.ctx || this.muted || !freq) return;
    const t = this.ctx.currentTime + delay;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (sweepTo) o.frequency.exponentialRampToValueAtTime(sweepTo, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g);
    g.connect(this.master);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  // Short filtered noise burst (used for crunchy bite / impact sounds)
  noise(dur, vol = 0.3, freq = 1800, q = 0.8, delay = 0) {
    if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime + delay;
    const n = Math.floor(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
    const src = this.ctx.createBufferSource(); src.buffer = buf;
    const f = this.ctx.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = freq; f.Q.value = q;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(f); f.connect(g); g.connect(this.master);
    src.start(t); src.stop(t + dur + 0.02);
  }

  sfx(name, vol = 1) {
    vibrate(name); // haptic feedback fires regardless of audio mute
    switch (name) {
      case "bite": // crunchy bite: two quick crunches + a happy chime
        this.noise(0.07, 0.5, 2600, 0.6);
        this.noise(0.09, 0.42, 1700, 0.7, 0.09);
        this.tone(880, 0.06, "triangle", 0.2, null, 0.16);
        this.tone(1175, 0.12, "triangle", 0.22, null, 0.21);
        break;
      case "hoof": // single horse hoof clop; vol scales with distance
        this.tone(150, 0.06, "triangle", 0.55 * vol, 70);
        this.tone(1100, 0.018, "square", 0.16 * vol, 350);
        break;
      case "hoofhi": // accented clop (gallop downbeat)
        this.tone(190, 0.07, "triangle", 0.7 * vol, 80);
        this.tone(1400, 0.02, "square", 0.2 * vol, 400);
        break;
      case "jump":
        this.tone(330, 0.15, "square", 0.3, 660);
        break;
      case "beer":
        this.tone(660, 0.07, "square", 0.25);
        this.tone(990, 0.1, "square", 0.25, null, 0.07);
        break;
      case "defeat":
        this.tone(220, 0.18, "sawtooth", 0.3, 70);
        break;
      case "hurt":
        this.tone(180, 0.25, "square", 0.35, 60);
        break;
      case "dance":
        this.tone(440, 0.08, "triangle", 0.3);
        this.tone(554, 0.08, "triangle", 0.3, null, 0.08);
        this.tone(659, 0.1, "triangle", 0.3, null, 0.16);
        break;
      case "fiesta":
        this.tone(523, 0.4, "square", 0.25);
        this.tone(659, 0.4, "triangle", 0.2, null, 0.02);
        this.tone(784, 0.4, "square", 0.2, null, 0.04);
        break;
      case "bosshit":
        this.tone(130, 0.16, "square", 0.4, 60);
        break;
      case "win":
        [523, 659, 784, 1047].forEach((f, i) => this.tone(f, 0.18, "square", 0.3, null, i * 0.12));
        break;
      case "gameover":
        [392, 330, 262, 196].forEach((f, i) => this.tone(f, 0.25, "sawtooth", 0.3, null, i * 0.18));
        break;
      case "siren":
        this.tone(760, 0.16, "square", 0.26);
        this.tone(560, 0.16, "square", 0.26, null, 0.16);
        this.tone(760, 0.16, "square", 0.26, null, 0.32);
        break;
      case "coin":
        this.tone(988, 0.06, "square", 0.28);
        this.tone(1319, 0.13, "square", 0.28, null, 0.05);
        break;
      case "pop":
        this.tone(880, 0.05, "square", 0.34, 220);
        this.tone(1500, 0.07, "triangle", 0.24, null, 0.02);
        break;
      case "cheer":
        [659, 784, 988].forEach((f, i) => this.tone(f, 0.24, "triangle", 0.3, f * 1.4, i * 0.03));
        this.tone(392, 0.32, "sawtooth", 0.14, 560, 0.02);
        this.tone(1319, 0.14, "square", 0.2, null, 0.06);
        break;
      default:
        break;
    }
  }

  startMusic(theme = 0) {
    this.stopMusic();
    this.resume();
    if (!this.ctx) return;
    this.theme = theme % MELODIES.length;
    const m = MELODIES[this.theme];
    this.step = 0;
    this.timer = setInterval(() => {
      const i = this.step % m.scale.length;
      this.tone(m.scale[i], 0.16, m.type || "square", 0.16);
      if (m.bass[i]) this.tone(m.bass[i], 0.22, "triangle", 0.18);
      this.step++;
    }, m.tempo);
  }

  stopMusic() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  destroy() {
    this.stopMusic();
    if (this.ctx) {
      try {
        this.ctx.close();
      } catch (e) {}
      this.ctx = null;
    }
  }
}
